import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useOrganization } from '@/hooks/useOrganization';
import { useHasRole } from '@/hooks/useHasRole';
import { clientSalesService } from '@/services/clients/clientSalesService';
import dayjs from 'dayjs';
import { queryKeys } from '@/lib/queryKeys';
import { STALE_TIME } from '@/lib/queryCacheConfig';

/**
 * Hook to fetch client sales report data for admin/manager users.
 * Aggregates sales by client (customer_name) within a date range.
 * 
 * @param from - Start date (optional)
 * @param to - End date (optional)
 * @returns Query result with client sales report data
 */
export const useClientSalesReport = (from?: Date, to?: Date) => {
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const { currentRole, isAdminOrManager } = useHasRole();
  const orgId = currentOrganization?.id ?? '';
  const fromKey = from ? dayjs(from).startOf('day').toISOString() : undefined;
  const toKey = to ? dayjs(to).endOf('day').toISOString() : undefined;

  const {
    data: clientSalesReport = [],
    isLoading,
    isPending,
    isFetching,
    error,
  } = useQuery({
    queryKey: queryKeys.clientSalesReport(orgId, fromKey, toKey),
    queryFn: async () => {
      if (!from || !to) {
        throw new Error('From and to dates are required');
      }
      if (!isAdminOrManager()) {
        throw new Error('Insufficient permissions: admin or manager role required');
      }
      return await clientSalesService.getClientSalesReport(
        currentOrganization.id,
        from,
        to,
        currentRole
      );
    },
    enabled: !!user && !!currentOrganization && isAdminOrManager() && !!from && !!to,
    staleTime: STALE_TIME.clientSalesReport,
  });

  return {
    clientSalesReport,
    isLoading,
    isPending,
    isFetching,
    error,
  };
};
